import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import type { Product } from '../types/product';
import { supabase } from '../lib/supabase';
import { mapRowToProduct } from '../data/productsFromSupabase';
import { getProductDetailPath as staticGetProductDetailPath } from '../data/products';

const USE_SUPABASE = process.env.REACT_APP_USE_SUPABASE_PRODUCTS === 'true';

/** Max items for Best Sellers and Featured when using Supabase (order = created_at desc). */
const BEST_SELLERS_LIMIT = 6;
const FEATURED_LIMIT = 2;

type ProductsState = {
  products: Product[];
  loading: boolean;
};

const defaultState: ProductsState = { products: [], loading: true };

const ProductsContext = createContext<ProductsState>(defaultState);

export function ProductsProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ProductsState>(defaultState);

  useEffect(() => {
    if (!USE_SUPABASE) {
      setState({ products: [], loading: false });
      return;
    }
    (async () => {
      try {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .order('created_at', { ascending: false });
        if (error) {
          setState({ products: [], loading: false });
          return;
        }
        const rows = data ?? [];
        const list = rows
          .filter((r) => r.is_available !== false)
          .map(mapRowToProduct);
        setState({ products: list, loading: false });
      } catch {
        setState({ products: [], loading: false });
      }
    })();
  }, []);

  return <ProductsContext.Provider value={state}>{children}</ProductsContext.Provider>;
}

function useProductsState() {
  const ctx = useContext(ProductsContext);
  return ctx ?? defaultState;
}

/** Only Supabase products are shown when REACT_APP_USE_SUPABASE_PRODUCTS=true. No static placeholders. */
export function useProducts() {
  const { products: supabaseProducts, loading } = useProductsState();
  const useSupabase = USE_SUPABASE;

  return useMemo(() => {
    const getProductBySlug = (slug: string | undefined) =>
      slug ? supabaseProducts.find((p) => p.slug === slug) ?? null : null;
    const getProductById = (id: string | undefined) =>
      id ? supabaseProducts.find((p) => p.id === id) ?? null : null;
    const getSamplePacks = () =>
      supabaseProducts.filter((p) => p.type === 'sample_pack');
    const getPresets = () =>
      supabaseProducts.filter((p) => p.type === 'preset');
    const getBestSellers = () =>
      supabaseProducts.slice(0, BEST_SELLERS_LIMIT);
    const getFeaturedProducts = () =>
      supabaseProducts.slice(0, FEATURED_LIMIT);
    const getProductDetailPath = (p: Product) => staticGetProductDetailPath(p);

    return {
      loading,
      useSupabase,
      getProductBySlug,
      getProductById,
      getSamplePacks,
      getPresets,
      getBestSellers,
      getFeaturedProducts,
      getProductDetailPath,
      // Expose raw list so feature-specific hooks (e.g. templates) can filter as needed.
      products: supabaseProducts,
    };
  }, [supabaseProducts, loading, useSupabase]);
}
